
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Mail, Lock, ArrowRight, Eye, EyeOff, Check, User, Phone,
  Zap, MessageSquare, CalendarDays, Briefcase, ChevronRight,
  ShieldCheck, Sparkles, Users as UsersIcon
} from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'

/* ================= DESIGN TOKENS ================= */
const C = {
  bg: '#f9f9ff',
  bgAlt: '#eff2ff',
  ink: '#000a1e',
  body: '#3c4148',
  primary: '#006b5f',
  primaryHover: '#005a50',
  navy: '#101f3f',
  gold: '#c9a24a',
}

const press =
  'transition-all duration-200 ease-out hover:scale-105 active:scale-95 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#006b5f]/30'

/* ================= REGISTER FORM ================= */
function RegisterForm({ onSubmit, loading, error }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
  })
  const [showPwd, setShowPwd] = useState(false)
  const [accept, setAccept] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!accept) return
    onSubmit(form)
  }

  // Force du mot de passe
  const pwdStrength = (() => {
    const len = form.password.length
    if (len === 0) return { level: 0, label: '', color: '' }
    if (len < 6) return { level: 1, label: 'Faible', color: 'bg-red-500' }
    if (len < 10) return { level: 2, label: 'Moyen', color: 'bg-amber-500' }
    return { level: 3, label: 'Fort', color: 'bg-[#006b5f]' }
  })()

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Nom complet */}
      <div>
        <label className="block text-sm font-semibold text-[#000a1e] mb-2">
          Nom complet
        </label>
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            required
            placeholder="Alexandre Martin"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full pl-12 pr-5 py-4 bg-[#f9f9ff] rounded-2xl border border-slate-200 text-base text-[#000a1e] placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] transition-all"
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-semibold text-[#000a1e] mb-2">
          Adresse email professionnelle
        </label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="email"
            required
            placeholder="alexandre@votredomaine.fr"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full pl-12 pr-5 py-4 bg-[#f9f9ff] rounded-2xl border border-slate-200 text-base text-[#000a1e] placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] transition-all"
          />
        </div>
      </div>

      {/* Téléphone */}
      <div>
        <label className="block text-sm font-semibold text-[#000a1e] mb-2">
          Téléphone mobile{' '}
          <span className="text-slate-400 font-normal">(optionnel)</span>
        </label>
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="tel"
            placeholder="+33 6 12 34 56 78"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full pl-12 pr-5 py-4 bg-[#f9f9ff] rounded-2xl border border-slate-200 text-base text-[#000a1e] placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] transition-all"
          />
        </div>
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-semibold text-[#000a1e] mb-2">
          Mot de passe
        </label>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type={showPwd ? 'text' : 'password'}
            required
            placeholder="••••••••"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full pl-12 pr-12 py-4 bg-[#f9f9ff] rounded-2xl border border-slate-200 text-base text-[#000a1e] placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] transition-all"
          />
          <button
            type="button"
            onClick={() => setShowPwd(!showPwd)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#000a1e] transition-colors"
            aria-label={showPwd ? 'Masquer' : 'Afficher'}
          >
            {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        {/* Force indicator */}
        {form.password && (
          <div className="flex items-center gap-3 mt-3">
            <div className="flex gap-1.5 flex-1">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-all ${
                    i <= pwdStrength.level ? pwdStrength.color : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
            <span
              className={`text-xs font-semibold ${
                pwdStrength.level === 1
                  ? 'text-red-500'
                  : pwdStrength.level === 2
                  ? 'text-amber-600'
                  : 'text-[#006b5f]'
              }`}
            >
              {pwdStrength.label}
            </span>
          </div>
        )}
      </div>

      {/* Confirm password */}
      <div>
        <label className="block text-sm font-semibold text-[#000a1e] mb-2">
          Confirmer le mot de passe
        </label>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type={showPwd ? 'text' : 'password'}
            required
            placeholder="••••••••"
            value={form.password_confirmation}
            onChange={(e) =>
              setForm({ ...form, password_confirmation: e.target.value })
            }
            className="w-full pl-12 pr-5 py-4 bg-[#f9f9ff] rounded-2xl border border-slate-200 text-base text-[#000a1e] placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] transition-all"
          />
        </div>
        {form.password &&
          form.password_confirmation &&
          form.password !== form.password_confirmation && (
            <p className="text-xs text-red-500 mt-2">
              Les mots de passe ne correspondent pas.
            </p>
          )}
      </div>

      {/* CGV */}
      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={accept}
          onChange={(e) => setAccept(e.target.checked)}
          className="w-5 h-5 rounded accent-[#006b5f] mt-0.5 flex-shrink-0"
        />
        <span className="text-sm text-[#3c4148] leading-relaxed">
          J'accepte les{' '}
          <span className="text-[#006b5f] font-semibold">
            Conditions Générales de Vente
          </span>{' '}
          et la{' '}
          <span className="text-[#006b5f] font-semibold">
            Politique de confidentialité
          </span>{' '}
          de Tapio Technologies SAS.
        </span>
      </label>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 animate-fade-in">
          {error}
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={loading || !accept}
        className={`${press} group relative overflow-hidden w-full bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-bold text-lg py-5 rounded-2xl shadow-lg shadow-[#006b5f]/20 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 shine`}
      >
        {loading ? (
          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Créer mon compte gratuitement
            <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
          </>
        )}
      </button>
    </form>
  )
}

/* ================= PAGE ================= */
export default function Register() {
  const navigate = useNavigate()
  const { register, loading } = useAuthStore()
  const [error, setError] = useState('')

  const handleRegister = async (form) => {
    setError('')
    if (form.password !== form.password_confirmation) {
      setError('Les mots de passe ne correspondent pas.')
      return
    }
    try {
      await register(form)
      navigate('/app/overview')
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Erreur lors de l'inscription. Vérifiez vos informations."
      )
    }
  }

  return (
    <div
      className="min-h-[calc(100vh-4rem)] flex items-stretch font-body text-[#3c4148] overflow-hidden"
      style={{ background: C.bg }}
    >
      {/* ================= ANIMATIONS CSS ================= */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
        .font-display { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; }
        .font-body { font-family: 'Inter', system-ui, sans-serif; }

        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
        @keyframes floatSm { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes heroIn { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes heroInX { from{opacity:0;transform:translateX(-20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes blob { 0%,100%{transform:scale(1) translate(0,0)} 50%{transform:scale(1.15) translate(-20px,-16px)} }
        @keyframes blobSlow { 0%,100%{transform:scale(1) translate(0,0)} 50%{transform:scale(1.1) translate(20px,16px)} }
        @keyframes shine { 0%{transform:translateX(-120%) skewX(-20deg)} 100%{transform:translateX(320%) skewX(-20deg)} }

        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-slow { animation: float 7.5s ease-in-out infinite reverse; }
        .animate-float-sm { animation: floatSm 5s ease-in-out infinite; }
        .animate-float-sm-d { animation: floatSm 5.5s ease-in-out .8s infinite; }
        .animate-fade-in { animation: fadeIn .25s ease-out both; }
        .animate-hero { animation: heroIn .8s cubic-bezier(.2,.7,.2,1) both; }
        .animate-in-x { animation: heroInX .6s cubic-bezier(.2,.7,.2,1) both; }
        .animate-in-up { animation: heroIn .7s cubic-bezier(.2,.7,.2,1) both; }
        .animate-blob { animation: blob 12s ease-in-out infinite; }
        .animate-blob-slow { animation: blobSlow 15s ease-in-out infinite; }
        .shine::after {
          content: ''; position: absolute; top: 0; left: 0; height: 100%; width: 35%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent);
          transform: translateX(-120%) skewX(-20deg);
        }
        .shine:hover::after { animation: shine .9s ease-out; }

        @media (prefers-reduced-motion: reduce) {
          *,*::before,*::after {
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: .01ms !important;
          }
        }
      `}</style>

      {/* ================= LEFT PANEL NAVY ================= */}
      <div
        className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden"
        style={{ background: C.navy }}
      >
        {/* Blobs animés */}
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(20,120,110,0.35),transparent_65%)] animate-blob pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(201,162,74,0.2),transparent_65%)] animate-blob-slow pointer-events-none" />

        <div className="relative z-10 text-white">
          <span className="animate-in-x inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/10 rounded-full text-xs font-semibold mb-6 backdrop-blur border border-white/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2dd4bf] animate-pulse" />
            REJOIGNEZ LA RÉVOLUTION TAPIO
          </span>

          <h1
            className="animate-in-up font-display text-4xl xl:text-5xl font-bold leading-[1.08] tracking-tight mb-4"
            style={{ animationDelay: '100ms' }}
          >
            Créez votre <br />
            espace en <br />
            2 minutes.
          </h1>

          <p
            className="animate-in-up text-base text-slate-300 mb-8 max-w-md leading-relaxed"
            style={{ animationDelay: '220ms' }}
          >
            Rejoignez plus de 15 000 professionnels qui digitalisent leurs
            échanges physiques avec Tapio.
          </p>

          {/* PHONE MOCKUP */}
          <div
            className="animate-in-up relative flex justify-center pt-2 pb-4"
            style={{ animationDelay: '340ms' }}
          >
            <div className="relative w-[260px]">
              <div className="animate-float">
                <div className="rounded-[38px] border-[8px] border-[#000a1e] bg-white overflow-hidden ml-8 h-[400px] shadow-2xl transition-transform duration-500 hover:scale-[1.03]">
                  <div className="mx-auto mt-2 w-20 h-4 bg-[#000a1e] rounded-full" />

                  <div className="mx-3 mt-2 h-[90px] rounded-2xl bg-gradient-to-br from-[#0b6a5d] to-[#3e8f83] relative">
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-white/90 text-[9px] font-bold text-[#000a1e] px-2 py-0.5 rounded-full">
                      <Sparkles className="w-3 h-3 text-[#006b5f]" /> Nouveau
                    </span>
                  </div>

                  <div className="relative -mt-7 mx-auto w-14 h-14 rounded-full bg-white border-4 border-white shadow" />

                  <div className="text-center px-3 mt-1">
                    <h3 className="font-display font-bold text-base text-[#000a1e]">
                      Votre profil Tapio
                    </h3>
                    <p className="text-[10px] font-medium text-[#3c4148]">
                      Prêt à être partagé
                    </p>
                    <p className="text-[9px] text-slate-500 mt-0.5">
                      En ligne dans 2 minutes
                    </p>
                  </div>

                  <div className="mx-3 mt-3 flex gap-1.5">
                    <button type="button" className="flex-1 text-[10px] font-semibold rounded-full py-2 bg-[#006b5f] text-white hover:scale-105 active:scale-95 transition-all">
                      Enregistrer contact
                    </button>
                    <button type="button" aria-label="Téléphone" className="w-8 h-8 rounded-full bg-[#e6ebff] flex items-center justify-center hover:scale-110 active:scale-90 transition-all">
                      <Phone className="w-3 h-3 text-[#000a1e]" />
                    </button>
                    <button type="button" aria-label="Message" className="w-8 h-8 rounded-full bg-[#dcf5ea] flex items-center justify-center hover:scale-110 active:scale-90 transition-all">
                      <MessageSquare className="w-3 h-3 text-[#006b5f]" />
                    </button>
                  </div>

                  <div className="mx-3 mt-2 space-y-1.5">
                    {[
                      { Icon: CalendarDays, label: 'Réserver un entretien' },
                      { Icon: Briefcase, label: 'Partager mon portfolio' },
                    ].map(({ Icon, label }) => (
                      <button
                        type="button"
                        key={label}
                        className="w-full flex items-center gap-2 bg-[#eff2ff] rounded-lg px-2.5 py-2 text-[10px] font-semibold text-[#000a1e] text-left transition-all hover:scale-[1.03] hover:bg-[#e3e9ff] active:scale-95"
                      >
                        <Icon className="w-3 h-3 text-[#006b5f]" />
                        <span className="flex-1">{label}</span>
                        <ChevronRight className="w-3 h-3 text-slate-500" />
                      </button>
                    ))}
                  </div>

                  <p className="text-center text-[8px] text-slate-500 mt-3">
                    <span className="inline-block w-1 h-1 rounded-full bg-[#006b5f] mr-1 animate-pulse" />
                    Tapio Instant Sync • vCard 4.0
                  </p>
                </div>
              </div>

              {/* Carte NFC flottante */}
              <div className="absolute -top-2 -left-10 w-[200px] h-[120px] animate-float-slow">
                <div className="w-full h-full rounded-xl bg-gradient-to-br from-[#161b2e] to-[#0a0e1c] shadow-2xl p-3 -rotate-[8deg] text-white transition-transform duration-500 hover:-rotate-[3deg] hover:scale-110 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold tracking-widest text-xs flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#2dd4bf] animate-pulse" />
                      TAPIO
                    </span>
                    <span className="w-5 h-3.5 rounded bg-gradient-to-br from-[#e6c76a] to-[#b8912f]" />
                  </div>
                  <div className="mt-6">
                    <p className="text-[8px] tracking-[0.2em] text-slate-400">CARTE PREMIUM</p>
                    <p className="font-display font-semibold text-sm">Votre nom ici</p>
                    <p className="text-[7px] tracking-[0.15em] text-slate-500 mt-1">NTAG216 MATTE STEEL</p>
                  </div>
                </div>
              </div>

              {/* Chip +2min */}
              <div className="absolute top-[180px] -right-12 animate-float-sm">
                <div className="bg-white rounded-xl shadow-xl px-3 py-2.5 flex items-center gap-2 transition-transform duration-300 hover:scale-110">
                  <span className="w-7 h-7 rounded-full bg-[#dcf5ea] flex items-center justify-center">
                    <Zap className="w-3.5 h-3.5 text-[#006b5f]" />
                  </span>
                  <div>
                    <p className="font-display font-bold text-sm leading-none text-[#000a1e]">2 min</p>
                    <p className="text-[9px] text-[#3c4148] mt-0.5">Mise en route</p>
                  </div>
                </div>
              </div>

              {/* Chip +15k users */}
              <div className="absolute bottom-0 -left-6 animate-float-sm-d">
                <div className="bg-white rounded-xl shadow-xl px-3 py-2.5 flex items-center gap-2 transition-transform duration-300 hover:scale-110">
                  <span className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center">
                    <UsersIcon className="w-3.5 h-3.5 text-[#000a1e]" />
                  </span>
                  <div>
                    <p className="font-display font-bold text-sm leading-none text-[#000a1e]">+15 000</p>
                    <p className="text-[9px] text-[#3c4148] mt-0.5">Professionnels actifs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div
            className="animate-in-up max-w-md mt-4 space-y-3"
            style={{ animationDelay: '460ms' }}
          >
            {[
              { icon: ShieldCheck, text: "Sans carte bancaire · Annulation en 1 clic" },
              { icon: Sparkles, text: "Design premium inclus dès l'inscription" },
              { icon: UsersIcon, text: "Communauté de 15 000+ professionnels" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3 text-sm text-slate-300">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#2dd4bf]" />
                </div>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer stats */}
        <div
          className="animate-in-up grid grid-cols-3 gap-3 relative z-10 mt-6 pt-5 border-t border-white/10"
          style={{ animationDelay: '580ms' }}
        >
          {[
            { icon: '⚡', label: '2 minutes', sub: 'Mise en route' },
            { icon: '💳', label: 'Zéro frais', sub: 'Sans abonnement' },
            { icon: '🛡️', label: 'RGPD Ready', sub: 'Données en France' },
          ].map((s, i) => (
            <div
              key={i}
              className="p-3 rounded-xl border border-white/10 backdrop-blur transition-all duration-300 hover:scale-105 hover:-translate-y-1"
              style={{ background: 'rgba(255,255,255,0.04)' }}
            >
              <div className="text-base mb-1">{s.icon}</div>
              <p className="text-[11px] font-semibold text-white">{s.label}</p>
              <p className="text-[9px] text-slate-400">{s.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ================= RIGHT FORM ================= */}
      <div
        className="flex-1 flex items-center justify-center p-8 lg:p-16 xl:p-20"
        style={{ background: C.bg }}
      >
        <div className="w-full max-w-lg xl:max-w-xl py-4">
          {/* Tabs premium */}
          <div className="flex items-center gap-1 mb-10 p-1.5 bg-[#eff2ff] rounded-2xl">
            <Link
              to="/login"
              className="flex-1 py-3.5 text-base font-medium text-slate-500 hover:text-[#000a1e] text-center transition-all rounded-xl"
            >
              Se connecter
            </Link>
            <button
              type="button"
              className="flex-1 py-3.5 text-base font-semibold bg-white rounded-xl shadow-sm text-[#000a1e]"
            >
              Créer un compte
            </button>
          </div>

          {/* Header */}
          <h2 className="font-display text-4xl xl:text-5xl font-bold text-[#000a1e] tracking-tight mb-3">
            Créez votre compte
          </h2>
          <p className="text-base text-[#3c4148] mb-10 leading-relaxed">
            Rejoignez Tapio en quelques secondes et commencez à digitaliser vos
            échanges dès aujourd'hui.
          </p>

          {/* Register Form */}
          <RegisterForm onSubmit={handleRegister} loading={loading} error={error} />

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-6 mt-8 text-xs text-slate-400">
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#006b5f]" />
              Sans carte bancaire
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#006b5f]" />
              Annulation en 1 clic
            </span>
          </div>

          {/* Already have account */}
          <p className="text-center text-base text-[#3c4148] mt-8">
            Déjà inscrit ?{' '}
            <Link
              to="/login"
              className="text-[#006b5f] font-semibold hover:underline"
            >
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
